import { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'

const pathOf = (url) => String(url || '').split('?')[0].toLowerCase()
const isImageUrl = (url) =>
  /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif|tiff?)$/.test(pathOf(url))

/**
 * True when an uploaded file should render as an embedded PDF/document rather than an <img>.
 * We can only embed when a signed URL is available (the media CDN serves only images), so a
 * legacy upload with just a media id stays on the image path.
 */
export function shouldRenderAsPdf(signedUrl) {
  return Boolean(signedUrl) && !isImageUrl(signedUrl)
}

// PDF open parameters (read client-side by the browser's native viewer, so they don't touch the
// signed-URL query/signature): hide the left thumbnail/bookmark sidebar and fit the page to the
// available width instead of a fixed zoom.
function withPdfViewerParams(url) {
  if (!url) return url
  const sep = url.includes('#') ? '&' : '#'
  return `${url}${sep}toolbar=1&navpanes=0&view=FitH`
}

/**
 * One uploaded prescription PDF, rendered in an <iframe> ONLY once it scrolls near the viewport
 * (IntersectionObserver) — so a patient with many past prescriptions doesn't spin up dozens of
 * iframes at once (kills low-spec machines). Off-screen PDFs never load; each loads at most once.
 * Mirrors the support drawer's uploaded-file rendering, made lazy for the long past-history list.
 */
export default function LazyUploadedPdf({ url, index = 0, total = 1, height = { xs: 480, sm: 760 } }) {
  const ref = useRef(null)
  const [mount, setMount] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (mount || !ref.current || typeof IntersectionObserver === 'undefined') {
      if (typeof IntersectionObserver === 'undefined') setMount(true) // no IO support → just load
      return undefined
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMount(true)
          io.disconnect()
        }
      },
      // Preload a little before it's visible so scrolling feels seamless.
      { rootMargin: '500px 0px' },
    )
    io.observe(ref.current)
    return () => io.disconnect()
  }, [mount])

  return (
    <Box
      ref={ref}
      sx={{
        border: '1px solid rgba(148, 163, 184, 0.3)',
        borderRadius: 2,
        overflow: 'hidden',
        background: '#fff',
        mb: 2,
        // Use the full available width of the viewer (no A4 cap) so the page scales up to fit.
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          backgroundColor: 'rgba(148, 163, 184, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <PictureAsPdfRoundedIcon fontSize="small" sx={{ color: '#dc2626' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {`Uploaded prescription${total > 1 ? ` (${index + 1}/${total})` : ''}`}
          </Typography>
        </Box>
        <Typography
          component="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          variant="body2"
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          Open <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', width: '100%', height }}>
        {!mount || !loaded ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : null}
        {mount ? (
          <Box
            component="iframe"
            src={withPdfViewerParams(url)}
            title={`Uploaded prescription ${index + 1}`}
            onLoad={() => setLoaded(true)}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          />
        ) : null}
      </Box>
    </Box>
  )
}
